import { DesktopDatePicker, LocalizationProvider, LoadingButton } from '@mui/lab';
import PaginationTable from 'components/tables/PaginationTable';
import {
  Container,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Card,
  CardContent,
  IconButton,
  TextField,
  Grid,
  FormLabel
} from '@mui/material';
import DateAdapter from '@mui/lab/AdapterDateFns';
import { format } from 'date-fns';
import { useBusinessEntityState, useHttpApi } from 'state';
import React, { useState, useEffect } from 'react';
import PDFDownloadIcon from 'assets/images/pdf-icon.png';
import CSVDownloadIcon from 'assets/images/csv-icon.png';
import { useSnackbar } from 'notistack';
import { saveAs } from 'file-saver';

const BTLTransactionReport = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { getTokenTransactionHistory, downloadTdaFeestransactionsReport } = useHttpApi();
  const { currentToken } = useBusinessEntityState();
  const [navData, setNavData] = useState([]);
  const [data, setData] = useState([]);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [page, setPage] = useState(1);
  const queryParams = `?page=${page}&from=${fromDate || ''}&to=${toDate || ''}`;
  const [totalCount, setTotalCount] = useState(0);
  const [showError, setShowError] = useState(false);

  const fetchTDATransactionHistory = async () => {
    if (!fromDate || !toDate) {
      setShowError(true);
      return;
    }
    const res = await getTokenTransactionHistory(currentToken?.token_id, queryParams);
    setData(res.results);
    setTotalCount(res.count);
  };

  const downloadTdaFeesReport = async (docFormat) => {
    if (!fromDate || !toDate) {
      return enqueueSnackbar('Please select a date range', { variant: 'error' });
    }
    const reportResponse = await downloadTdaFeestransactionsReport(
      `?token_id=${currentToken?.token_id}&start_date=${fromDate}&end_date=${toDate}&doc_format=${docFormat}`
    );
    const blob = new Blob([reportResponse], {
      type: docFormat === 'pdf' ? 'application/pdf' : 'text/csv'
    });
    saveAs(blob, `tda_fees_transactions_report.${docFormat}`);
  };

  // UseEffects

  useEffect(() => {
    if (fromDate && toDate) {
      fetchTDATransactionHistory();
    }
  }, [page]);

  const headCells = [
    {
      accessor: 'investor.investor_name',
      Header: 'Customer Name',
      show: true
    },
    {
      accessor: 'investor.investor_email_id',
      Header: 'Email',
      show: true
    },
    {
      accessor: 'investor.investor_phone_no',
      Header: 'Phone No',
      show: true
    },
    {
      accessor: 'transaction_date',
      Header: 'Transaction Date',
      show: true
    },

    {
      accessor: 'transaction_type',
      Header: 'Transaction Type',
      show: true
    },
    {
      accessor: 'transaction_token_price',
      Header: 'Transaction Price/Token ($)',
      show: true
    },
    {
      accessor: 'token_qty',
      Header: 'Quantity',
      show: true
    },
    // {
    //   accessor: 'nav',
    //   Header: 'Last Reset Price/Token ($)',
    //   show: true
    // },
    {
      accessor: 'sales_consideration',
      Header: 'Trade Value ($)',
      show: true
    },
    {
      accessor: 'fees_amount',
      Header: 'Platform Fees ($)',
      show: true
    },
    {
      accessor: 'total_transaction_amount_paid_by_investor',
      Header: 'Total Paid By Investor ($)',
      show: true
    },
    {
      accessor: 'total_transaction_amount_paid_to_investor',
      Header: 'Total Paid To Investor ($)',
      show: true
    },
    {
      accessor: 'investor_profit_or_loss_amount',
      Header: 'Investor Profit/Loss ($)',
      show: true
    }
  ];
  const updateMyData = (rowIndex, columnId, value) => {
    // We also turn on the flag to not reset the page
    setSkipPageReset(true);
    setData((old) =>
      old.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...old[rowIndex],
            [columnId]: value
          };
        }
        return row;
      })
    );
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Card>
        <CardContent>
          {/* Filter */}
          <Grid
            container
            spacing={1}
            sx={{ width: '100%', flexDirection: 'row', flexWrap: 'wrap' }}
          >
            <Grid item lg={3} md={4} sm={8} xs={8}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DesktopDatePicker
                  label="From Date *"
                  fullWidth
                  size="small"
                  inputFormat="MM-dd-yyyy"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(format(e, 'yyyy-MM-dd'));
                  }}
                  renderInput={(params) => (
                    <TextField
                      FormHelperTextProps={{ sx: { color: 'red' } }}
                      fullWidth
                      size="small"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true // Prevent manual typing
                      }}
                      helperText={showError && !fromDate && 'Select a date'}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item lg={3} md={4} sm={8} xs={8}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DesktopDatePicker
                  label="To Date *"
                  fullWidth
                  size="small"
                  inputFormat="MM-dd-yyyy"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(format(e, 'yyyy-MM-dd'));
                  }}
                  renderInput={(params) => (
                    <TextField
                      FormHelperTextProps={{ sx: { color: 'red' } }}
                      fullWidth
                      size="small"
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true // Prevent manual typing
                      }}
                      helperText={showError && !toDate && 'Select a date'}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item lg={3} md={4} sm={12} xs={12} container>
              <LoadingButton
                disabled={!fromDate || !toDate}
                variant="gradient"
                loadingPosition="start"
                onClick={fetchTDATransactionHistory}
              >
                Filter
              </LoadingButton>
              <Box
                sx={{
                  height: '2.5rem',
                  width: '2.5rem',
                  borderRadius: '0.2rem',
                  backgroundColor: '#fff',
                  color: '#000',
                  boxShadow: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  marginX: 1
                }}
                onClick={async () => {
                  await downloadTdaFeesReport('pdf');
                }}
              >
                <Box
                  component="img"
                  sx={{
                    p: 1
                  }}
                  src={PDFDownloadIcon}
                />
              </Box>
              <Box
                sx={{
                  height: '2.5rem',
                  width: '2.5rem',
                  borderRadius: '0.2rem',
                  backgroundColor: '#fff',
                  color: '#000',
                  boxShadow: 4,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
                onClick={async () => {
                  await downloadTdaFeesReport('csv');
                }}
              >
                <Box
                  component="img"
                  sx={{
                    p: 1
                  }}
                  src={CSVDownloadIcon}
                />
              </Box>
            </Grid>
            
          </Grid>
          <Typography mt={2}>
            <strong>Token:</strong> {currentToken?.token_name} | {currentToken?.token_symbol}
          </Typography>
          {/* Table */}
          <PaginationTable
            handlePageUpdate={setPage}
            tableTitle=""
            columns={headCells}
            refreshFunction={fetchTDATransactionHistory}
            data={data}
            setData={setData}
            updateMyData={updateMyData}
            skipPageReset={skipPageReset}
            count={totalCount}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default BTLTransactionReport;
